import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, BookOpen, Flower2, Hexagon, Sparkles, Sun } from "lucide-react";
import { api, formatPrice, resolveImage } from "@/lib/api";
import { IMAGES } from "@/constants/images";
import ProductCard from "@/components/ProductCard";
import CategoriesSection from "@/components/CategoriesSection";
import NewsletterForm from "@/components/NewsletterForm";
import { Seo } from "@/context/SeoContext";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

export default function HomePage() {
  const [featured, setFeatured] = useState([]);
  const [posts, setPosts] = useState([]);
  const [spotlight, setSpotlight] = useState(null);

  useEffect(() => {
    api.get("/products").then(({ data }) => setFeatured(data.filter((p) => p.featured).slice(0, 4))).catch(() => {});
    api.get("/blog").then(({ data }) => setPosts(data.slice(0, 3))).catch(() => {});
    api.get("/spotlight").then(({ data }) => setSpotlight(data.enabled ? data : null)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <Seo pageKey="home" />
      <section data-testid="hero-section" className="relative overflow-hidden">
        <div className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-amber-glow/20 blur-3xl" />
        <div className="pointer-events-none absolute -left-32 bottom-0 h-80 w-80 rounded-full bg-sage-light blur-3xl" />
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-16 sm:px-8 lg:grid-cols-12 lg:py-24">
          <motion.div {...fadeUp} className="space-y-8 lg:col-span-7">
            {spotlight?.product && (
              <Link
                to={`/produit/${spotlight.product.product_id}`}
                data-testid="hero-spotlight"
                className="group block w-44 overflow-hidden rounded-2xl border border-amber-deep/20 bg-white shadow-sm shadow-amber-900/5 transition-all duration-200 hover:-translate-y-1 hover:border-amber-deep hover:shadow-md sm:w-48"
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-sand">
                  {spotlight.product.image_url ? (
                    <img
                      src={resolveImage(spotlight.product.image_url)}
                      alt={spotlight.product.name}
                      className="h-full w-full bg-white object-contain p-1.5 transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <span className="flex h-full w-full items-center justify-center text-amber-deep">
                      <Sparkles size={28} />
                    </span>
                  )}
                  <span className="absolute left-2 top-2 rounded-full bg-amber-deep px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white shadow">
                    {spotlight.label}
                  </span>
                </div>
                <div className="px-3 py-2.5">
                  <p className="truncate text-sm font-medium text-bark">{spotlight.product.name}</p>
                  {spotlight.product.price_cents > 0 && (
                    <p className="mt-0.5 text-sm font-semibold text-amber-deep">{formatPrice(spotlight.product.price_cents)}</p>
                  )}
                </div>
              </Link>
            )}
            <p className="eyebrow" data-testid="hero-eyebrow">Miellerie artisanale</p>
            <h1 className="font-serif text-4xl font-semibold leading-tight tracking-tight text-bark sm:text-5xl lg:text-6xl" data-testid="hero-title">
              Le miel des <span className="italic text-amber-deep">ruches Alpines</span>
            </h1>
            <p className="max-w-xl text-base leading-relaxed text-bark-soft sm:text-lg">
              Depuis 2003, nos abeilles butinent acacias, châtaigniers, fleurs des Alpes et autres au grès des envies de notre apiculteur.
              Chaque pot est récolté à maturité et extrait à froid.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/boutique"
                data-testid="hero-cta-boutique"
                className="flex items-center gap-2 rounded-full bg-amber-deep px-7 py-3.5 font-medium text-white shadow-md shadow-amber-900/15 transition-all duration-200 hover:bg-honey active:scale-95"
              >
                Explorer la récolte <ArrowRight size={18} />
              </Link>
              <Link
                to="/histoire"
                data-testid="hero-cta-histoire"
                className="rounded-full border-2 border-amber-700/30 px-7 py-3.5 font-medium text-amber-900 transition-all duration-200 hover:border-amber-deep hover:bg-amber-50"
              >
                L'histoire du rucher
              </Link>
            </div>
            <div className="flex flex-wrap gap-x-8 gap-y-3 pt-2 text-sm text-bark-soft">
              <span className="flex items-center gap-2"><Sun size={16} className="text-honey" /> Extraction à froid</span>
              <span className="flex items-center gap-2"><Flower2 size={16} className="text-sage" /> 6 terroirs d'exception</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="relative lg:col-span-5"
          >
            <div className="overflow-hidden rounded-[2rem] border border-amber-900/10 shadow-2xl shadow-amber-900/10">
              <img src={IMAGES.honeycomb} alt="Rayon de miel doré" className="aspect-[4/5] w-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 animate-float rounded-2xl border border-amber-900/10 bg-white/90 px-5 py-4 shadow-lg backdrop-blur" data-testid="hero-floating-badge">
              <p className="font-serif text-lg font-semibold text-bark">Récolte {new Date().getFullYear()}</p>
              <p className="text-xs uppercase tracking-[0.15em] text-stone-mute">100 % artisanal</p>
            </div>
            <div className="absolute -right-4 -top-4 flex h-20 w-20 animate-spin-slow items-center justify-center rounded-full bg-sage-light text-sage">
              <Hexagon size={26} className="fill-sage/30" />
            </div>
          </motion.div>
        </div>
      </section>

      <section data-testid="bento-section" className="mx-auto max-w-7xl px-4 py-16 sm:px-8">
        <motion.div {...fadeUp} className="mb-10">
          <p className="eyebrow">Les trésors de la ruche</p>
          <h2 className="mt-2 font-serif text-2xl font-medium tracking-tight text-bark sm:text-3xl lg:text-4xl">
            Bien plus que du miel
          </h2>
        </motion.div>
        <div className="grid gap-6 md:grid-cols-3">
          <motion.div {...fadeUp} className="relative overflow-hidden rounded-2xl border border-amber-900/10 md:col-span-2 md:row-span-2">
            <img src={IMAGES.blossoms} alt="Ruches au milieu des fleurs" className="h-full min-h-[320px] w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-beeswax/70 via-beeswax/10 to-transparent" />
            <div className="absolute bottom-0 p-8">
              <p className="eyebrow !text-amber-glow">Notre engagement</p>
              <p className="mt-2 max-w-md font-serif text-2xl font-medium text-cream">
                Une apiculture douce, au rythme des saisons et des abeilles.
              </p>
              <Link to="/histoire" data-testid="bento-story-link" className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-amber-glow hover:text-white">
                Découvrir notre histoire <ArrowRight size={15} />
              </Link>
            </div>
          </motion.div>
          <motion.div {...fadeUp} className="rounded-2xl border border-amber-800/15 bg-white p-8 shadow-sm">
            <Flower2 size={28} className="text-honey" />
            <h3 className="mt-4 font-serif text-xl font-semibold text-bark">Miels de saison</h3>
            <p className="mt-2 text-sm leading-relaxed text-bark-soft">
              Lavande, acacia, châtaignier, montagne : chaque récolte est un voyage, chaque terroir a sa voix.
            </p>
          </motion.div>
          <motion.div {...fadeUp} className="rounded-2xl border border-amber-800/15 bg-sage-light p-8 shadow-sm">
            <Sparkles size={28} className="text-sage" />
            <h3 className="mt-4 font-serif text-xl font-semibold text-bark">Cire &amp; propolis</h3>
            <p className="mt-2 text-sm leading-relaxed text-bark-soft">
              Bougies coulées main, gelée royale fraîche, pollen de garrigue : rien ne se perd dans la ruche.
            </p>
          </motion.div>
        </div>
        <motion.div {...fadeUp} className="mt-6 grid gap-6 rounded-2xl border border-amber-800/15 bg-sand p-8 text-center sm:grid-cols-3">
          {[
            { value: "", label: "Des ruches choyées" },
            { value: "6", label: "terroirs de récolte" },
            { value: "2003", label: "année du premier essaim" },
          ].map((stat) => (
            <div key={stat.label} data-testid={`stat-${stat.label.split(" ")[0]}`} className="flex flex-col justify-end">
              {stat.value ? (
                <p className="font-serif text-4xl font-semibold text-amber-deep">{stat.value}</p>
              ) : null}
              <p className={`text-sm uppercase tracking-[0.15em] text-bark-soft ${stat.value ? "mt-1" : ""}`}>{stat.label}</p>
            </div>
          ))}
        </motion.div>
      </section>

      <CategoriesSection />

      <section data-testid="featured-section" className="bg-sand/60">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8">
          <motion.div {...fadeUp} className="mb-10 flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="eyebrow">La récolte du moment</p>
              <h2 className="mt-2 font-serif text-2xl font-medium tracking-tight text-bark sm:text-3xl lg:text-4xl">
                Nos douceurs préférées
              </h2>
            </div>
            <Link to="/boutique" data-testid="featured-see-all" className="flex items-center gap-2 text-sm font-medium text-amber-deep hover:text-bark">
              Toute la boutique <ArrowRight size={15} />
            </Link>
          </motion.div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {featured.map((p) => (
              <motion.div key={p.product_id} {...fadeUp} className="h-full">
                <ProductCard product={p} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="gazette-teaser" className="mx-auto max-w-7xl px-4 py-16 sm:px-8">
        <motion.div {...fadeUp} className="mb-10">
          <p className="eyebrow flex items-center gap-2"><BookOpen size={14} /> La Gazette de la Ruche</p>
          <h2 className="mt-2 font-serif text-2xl font-medium tracking-tight text-bark sm:text-3xl lg:text-4xl">
            Recettes &amp; nouvelles du rucher
          </h2>
        </motion.div>
        <div className="grid gap-6 md:grid-cols-3">
          {posts.map((post) => (
            <motion.article key={post.slug} {...fadeUp} data-testid={`gazette-card-${post.slug}`} className="group overflow-hidden rounded-2xl border border-amber-800/15 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
              <Link to={`/gazette/${post.slug}`} data-testid={`gazette-link-${post.slug}`}>
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={post.image_url} alt={post.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-6">
                  <p className="text-xs uppercase tracking-[0.15em] text-amber-deep">{post.tag} · {post.read_time}</p>
                  <h3 className="mt-2 font-serif text-xl font-semibold leading-snug text-bark group-hover:text-amber-deep">{post.title}</h3>
                  <p className="mt-2 line-clamp-2 text-sm text-bark-soft">{post.excerpt}</p>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>
      </section>

      <section data-testid="newsletter-band" className="border-y border-amber-900/10 bg-beeswax">
        <div className="mx-auto flex max-w-7xl flex-col items-start gap-6 px-4 py-14 sm:px-8 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="font-serif text-2xl font-medium text-cream sm:text-3xl">Recevez notre Gazette Champêtre</h2>
            <p className="mt-2 text-sm text-cream/60">Une lettre par saison : recettes, récoltes et vie de la ruche.</p>
          </div>
          <div className="w-full max-w-md">
            <NewsletterForm variant="footer" />
          </div>
        </div>
      </section>
    </div>
  );
}
